import { io, Socket } from 'socket.io-client';

import { SOCKETIO_PATH, WS_GATEWAY_URL } from '@frontend/envConfig';

type ManagerReservedEventNames =
  | 'reconnect'
  | 'reconnect_attempt'
  | 'reconnect_error'
  | 'reconnect_failed'
  | 'ping';

class SocketService {
  private socket: Socket | null = null;

  init(optiIdAccessToken: string, instance_id: string) {
    // Clean up previous socket if it exists to prevent orphaned connections
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(WS_GATEWAY_URL, {
      path: SOCKETIO_PATH,
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: false,
      auth: {
        opti_id_access_token: optiIdAccessToken,
        instance_id,
      },

      // Reconnection configuration
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
    });

    return this.socket;
  }

  connect() {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot connect.');
      return;
    }

    console.warn('Connecting socket to server...');
    this.socket.connect();
  }

  disconnect() {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot disconnect.');
      return;
    }

    console.warn('Disconnecting socket from server...');
    this.socket.disconnect();
  }

  emit(event: string, data?: unknown) {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot emit event:', event);
      return;
    }
    this.socket.emit(event, data);
  }

  on(event: string, callback: (data?: unknown) => void) {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot subscribe to event:', event);
      return;
    }
    this.socket.on(event, callback);
  }

  off(event: string, callback?: (data?: unknown) => void) {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot unsubscribe from event:', event);
      return;
    }
    this.socket.off(event, callback);
  }

  onManager(event: ManagerReservedEventNames, callback: (...args: unknown[]) => void): void {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot subscribe to manager event:', event);
      return;
    }

    this.socket.io.on(event, callback);
  }

  offManager(event: ManagerReservedEventNames, callback?: (...args: unknown[]) => void): void {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot unsubscribe from manager event:', event);
      return;
    }

    this.socket.io.off(event, callback);
  }

  isConnected() {
    return this.socket?.connected ?? false;
  }

  getSocket() {
    return this.socket;
  }
}

export const socketService = new SocketService();
